import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuSeparator,
    ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { Heart, Calendar, Plus } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Difficulty, MealCategory, Tag } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { getCorrectImageUrl } from '@/utils/imageUtils';

interface RecipeCardProps {
    id: number;
    title: string;
    time: string;
    difficulty: Difficulty;
    image?: string;
    category: MealCategory;
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    tags: Tag[];
    ingredients?: string[];
    instructions?: string;
    onAddToWeeklyMenu?: (recipeId: number, recipeTitle: string) => void;
    showRecommendationBadge?: boolean;
    isFavorite?: boolean; // ← NUEVO prop
    toggleFavorite?: (recipeId: number) => Promise<boolean>; // ← NUEVO prop
}

const RecipeCard = ({
    id,
    title,
    time,
    difficulty,
    image,
    category,
    calories,
    protein,
    carbs,
    fat,
    tags = [],
    onAddToWeeklyMenu,
    showRecommendationBadge = false,
    isFavorite = false, // ← Valor por defecto
    toggleFavorite, // ← Función opcional
}: RecipeCardProps) => {
    const { toast } = useToast();
    const { isLoggedIn } = useAuth();
    const [isToggling, setIsToggling] = useState(false);
    const imageUrl = getCorrectImageUrl(image);
    // ✅ ELIMINADO: El hook problemático que causaba el loop
    // const { favoriteIds, toggleFavorite, loading: favoriteLoading } = useFavorites();
    
    // ✅ USAMOS el prop en su lugar
    const isFav = isFavorite;

    const difficultyMap = {
        [Difficulty.FÁCIL]: { text: 'fácil', color: 'bg-green-100 text-green-800' },
        [Difficulty.MEDIO]: { text: 'medio', color: 'bg-amber-100 text-amber-800' },
        [Difficulty.DIFÍCIL]: { text: 'difícil', color: 'bg-red-100 text-red-800' }
    };

    const getDifficultyInfo = (diff: Difficulty) => {
        return difficultyMap[diff] || { text: 'medio', color: 'bg-amber-100 text-amber-800' };
    };

    const categoryMap = {
        [MealCategory.DESAYUNO]: 'Desayuno',
        [MealCategory.COMIDA]: 'Comida',
        [MealCategory.CENA]: 'Cena',
        [MealCategory.SNACK]: 'Snack'
    };

    const tagMap: { [key in Tag]: { text: string; color: string; } } = {
        [Tag.VEGETARIANA]: { text: 'Vegetariano', color: 'bg-green-100 text-green-800' },
        [Tag.VEGANA]: { text: 'Vegano', color: 'bg-emerald-100 text-emerald-800' },
        [Tag.ALTA_PROTEINA]: { text: 'Alto en Proteína', color: 'bg-purple-100 text-purple-800' },
        [Tag.BAJO_EN_CARBOS]: { text: 'Bajo en Carbos', color: 'bg-blue-100 text-blue-800' },
        [Tag.GLUTEN_FREE]: { text: 'Sin Gluten', color: 'bg-yellow-100 text-yellow-800' },
        [Tag.SIN_LACTOSA]: { text: 'Sin Lácteos', color: 'bg-pink-100 text-pink-800' },
        [Tag.RÁPIDA]: { text: 'Comida Rápida', color: 'bg-orange-100 text-orange-800' },
        [Tag.KETO]: { text: 'Keto', color: 'bg-indigo-100 text-indigo-800' },
        [Tag.ALTA_FIBRA]: { text: 'Alta Fibra', color: 'bg-teal-100 text-teal-800' },
        [Tag.BAJO_EN_CALORIAS]: { text: 'Bajas Calorías', color: 'bg-cyan-100 text-cyan-800' },
        [Tag.BAJO_EN_GRASAS]: { text: 'Bajo en Grasas', color: 'bg-lime-100 text-lime-800' },
    };

    const handleToggleFavorite = async (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (!isLoggedIn) {
            toast({
                title: "Inicia sesión",
                description: "Debes iniciar sesión para agregar recetas a favoritos",
                variant: "destructive",
            });
            return;
        }

        if (!toggleFavorite) {
            console.error('❌ toggleFavorite function not provided to RecipeCard');
            toast({
                title: "Error",
                description: "Función de favoritos no disponible",
                variant: "destructive",
            });
            return;
        }

        if (isToggling) return;

        setIsToggling(true);
        try {
            const success = await toggleFavorite(id); // ← Usa la función del prop
            if (success) {
                toast({
                    title: isFav ? "Eliminado de favoritos" : "Agregado a favoritos",
                    description: isFav
                        ? `${title} se ha eliminado de tus favoritos.`
                        : `${title} se ha agregado a tus favoritos.`,
                });
            } else {
                toast({
                    title: "Error",
                    description: "No se pudo actualizar favoritos",
                    variant: "destructive",
                });
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
            toast({
                title: "Error",
                description: "Ocurrió un error al actualizar favoritos",
                variant: "destructive",
            });
        } finally {
            setIsToggling(false);
        }
    };

    const addToWeeklyMenu = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        if (onAddToWeeklyMenu) {
            onAddToWeeklyMenu(id, title);
        }
    };

    const defaultImage = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80';

    return (
        <ContextMenu>
            <ContextMenuTrigger>
                <Link to={`/recipe/${id}`} className="block h-full">
                    <Card className="recipe-card h-full flex flex-col transition-transform duration-300 hover:shadow-md hover:-translate-y-1 relative">
                        {showRecommendationBadge && (
                            <div className="absolute top-3 left-3 z-10">
                                <Badge className="bg-emerald-500 hover:bg-emerald-600 text-white">
                                    Recomendado
                                </Badge>
                            </div>
                        )}
                        
                        <div className="relative h-48 overflow-hidden">
                            <img
                                src={imageUrl || defaultImage}
                                alt={title}
                                className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                            />
                            <Badge className="absolute top-3 left-3 bg-white/80 text-food-purple hover:bg-white">
                                {categoryMap[category]}
                            </Badge>
                            <Button
                                variant="ghost"
                                size="icon"
                                className={`absolute top-3 right-3 h-8 w-8 ${isFav ? 'text-red-500' : 'text-gray-400'
                                    } hover:text-red-500 bg-white/80 hover:bg-white transition-colors ${isToggling ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                                onClick={handleToggleFavorite}
                                disabled={isToggling || !toggleFavorite}
                            >
                                <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''} ${isToggling ? 'animate-pulse' : ''
                                    }`} />
                            </Button>
                        </div>
                        <CardHeader className="pb-2">
                            <CardTitle className="text-lg font-bold line-clamp-1">{title}</CardTitle>
                            <CardDescription className="flex justify-between items-center">
                                <span className="text-sm">{time}</span>
                                <Badge variant="outline" className={`text-xs ${getDifficultyInfo(difficulty).color}`}>
                                    {getDifficultyInfo(difficulty).text}
                                </Badge>
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="flex-grow">
                            <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
                                <div><span className="font-medium">Cal:</span> {calories}</div>
                                <div><span className="font-medium">Proteína:</span> {protein}g</div>
                                <div><span className="font-medium">Carbos:</span> {carbs}g</div>
                                <div><span className="font-medium">Grasas:</span> {fat}g</div>
                            </div>

                            {tags.length > 0 && (
                                <div className="flex flex-wrap gap-1 mb-2">
                                    {tags.slice(0, 3).map((tag) => (
                                        <Badge
                                            key={tag}
                                            variant="outline"
                                            className={`text-xs ${tagMap[tag]?.color || 'bg-gray-100 text-gray-800'
                                                }`}
                                        >
                                            {tagMap[tag]?.text || tag}
                                        </Badge>
                                    ))}
                                    {tags.length > 3 && (
                                        <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
                                            +{tags.length - 3}
                                        </Badge>
                                    )}
                                </div>
                            )}

                            {/* <p className="text-sm text-gray-500 line-clamp-2">
                                Una deliciosa receta perfecta para cualquier ocasión.
                            </p> */}
                        </CardContent>
                        <CardFooter className="pt-2">
                            <div className="text-xs text-gray-400 flex items-center justify-between w-full">
                                {/* <span>4.5 ★★★★☆</span> */}
                                {isFav && (
                                    <div className="flex items-center gap-1 text-red-500">
                                        <Heart className="h-3 w-3 fill-current" />
                                        <span>Favorita</span>
                                    </div>
                                )}
                            </div>
                        </CardFooter>
                    </Card>
                </Link>
            </ContextMenuTrigger>
            <ContextMenuContent className="w-48">
                <ContextMenuItem onClick={addToWeeklyMenu}>
                    <Calendar className="mr-2 h-4 w-4" />
                    Agregar al Menú Semanal
                </ContextMenuItem>
                <ContextMenuItem
                    onClick={handleToggleFavorite}
                    disabled={isToggling || !isLoggedIn || !toggleFavorite}
                >
                    <Heart className={`mr-2 h-4 w-4 ${isFav ? 'text-red-500 fill-current' : ''}`} />
                    {isFav ? 'Quitar de Favoritos' : 'Agregar a Favoritos'}
                </ContextMenuItem>
                <ContextMenuSeparator />
                <ContextMenuItem>
                    <Plus className="mr-2 h-4 w-4" />
                    Acciones Rápidas
                </ContextMenuItem>
            </ContextMenuContent>
        </ContextMenu>
    );
};

export default RecipeCard;
// import React, { useState, useEffect } from 'react';
// import { Link } from 'react-router-dom';
// import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
// import { Badge } from '@/components/ui/badge';
// import { Button } from '@/components/ui/button';
// import {
//     ContextMenu,
//     ContextMenuContent,
//     ContextMenuItem,
//     ContextMenuSeparator,
//     ContextMenuTrigger,
// } from '@/components/ui/context-menu';
// import { Heart, Calendar, Plus } from 'lucide-react';
// import { useToast } from '@/hooks/use-toast';
// import { Difficulty, MealCategory, Tag } from '@/types';
// import { useFavorites } from '@/hooks/useFavorites';
// import { useAuth } from '@/contexts/AuthContext';

// interface RecipeCardProps {
//     id: number;
//     title: string;
//     time: string;
//     difficulty: Difficulty;
//     image?: string;
//     category: MealCategory;
//     calories: number;
//     protein: number;
//     carbs: number;
//     fat: number;
//     tags: Tag[];
//     ingredients?: string[];
//     instructions?: string;
//     onAddToWeeklyMenu?: (recipeId: number, recipeTitle: string) => void;
//     showRecommendationBadge?: boolean;
// }



// const RecipeCard = ({
//     id,
//     title,
//     time,
//     difficulty,
//     image,
//     category,
//     calories,
//     protein,
//     carbs,
//     fat,
//     tags = [],
//     onAddToWeeklyMenu,
//     showRecommendationBadge = false,
// }: RecipeCardProps) => {
//     const { toast } = useToast();
//     const { isLoggedIn } = useAuth();
//     const { favoriteIds, toggleFavorite, loading: favoriteLoading } = useFavorites();
//     const [isFav, setIsFav] = useState(false);
//     const [isToggling, setIsToggling] = useState(false);
    

//     // Sincronizar el estado local con el hook de favoritos
// useEffect(() => {
//     setIsFav(favoriteIds.includes(id)); // Usa favoriteIds directamente
// }, [favoriteIds, id]);

//     const difficultyMap = {
//         [Difficulty.FÁCIL]: { text: 'fácil', color: 'bg-green-100 text-green-800' },
//         [Difficulty.MEDIO]: { text: 'medio', color: 'bg-amber-100 text-amber-800' },
//         [Difficulty.DIFÍCIL]: { text: 'difícil', color: 'bg-red-100 text-red-800' }
//     };

//     // Agrega un valor por defecto para dificultades desconocidas
//     const getDifficultyInfo = (diff: Difficulty) => {
//         return difficultyMap[diff] || { text: 'medio', color: 'bg-amber-100 text-amber-800' };
//     };


//     // Mapeo de categorías a español
//     const categoryMap = {
//         [MealCategory.DESAYUNO]: 'Desayuno',
//         [MealCategory.COMIDA]: 'Comida',
//         [MealCategory.CENA]: 'Cena',
//         [MealCategory.SNACK]: 'Snack'
//     };

//     // Mapeo de tags a español
//     const tagMap: { [key in Tag]: { text: string; color: string; } } = {
//         [Tag.VEGETARIANA]: { text: 'Vegetariano', color: 'bg-green-100 text-green-800' },
//         [Tag.VEGANA]: { text: 'Vegano', color: 'bg-emerald-100 text-emerald-800' },
//         [Tag.ALTA_PROTEINA]: { text: 'Alto en Proteína', color: 'bg-purple-100 text-purple-800' },
//         [Tag.BAJO_EN_CARBOS]: { text: 'Bajo en Carbos', color: 'bg-blue-100 text-blue-800' },
//         [Tag.GLUTEN_FREE]: { text: 'Sin Gluten', color: 'bg-yellow-100 text-yellow-800' },
//         [Tag.SIN_LACTOSA]: { text: 'Sin Lácteos', color: 'bg-pink-100 text-pink-800' },
//         [Tag.RÁPIDA]: { text: 'Comida Rápida', color: 'bg-orange-100 text-orange-800' },
//         [Tag.KETO]: { text: 'Keto', color: 'bg-indigo-100 text-indigo-800' },
//         [Tag.ALTA_FIBRA]: { text: 'Alta Fibra', color: 'bg-teal-100 text-teal-800' },
//         [Tag.BAJO_EN_CALORIAS]: { text: 'Bajas Calorías', color: 'bg-cyan-100 text-cyan-800' },
//         [Tag.BAJO_EN_GRASAS]: { text: 'Bajo en Grasas', color: 'bg-lime-100 text-lime-800' },
//     };

//     const handleToggleFavorite = async (e: React.MouseEvent) => {
//         e.preventDefault();
//         e.stopPropagation();

//         if (!isLoggedIn) {
//             toast({
//                 title: "Inicia sesión",
//                 description: "Debes iniciar sesión para agregar recetas a favoritos",
//                 variant: "destructive",
//             });
//             return;
//         }

//         if (isToggling) return;

//         setIsToggling(true);
//         try {
//             const success = await toggleFavorite(id);
//             if (success) {
//                 const newFavState = !isFav;
//                 setIsFav(newFavState);
//                 toast({
//                     title: newFavState ? "Agregado a favoritos" : "Eliminado de favoritos",
//                     description: newFavState
//                         ? `${title} se ha agregado a tus favoritos.`
//                         : `${title} se ha eliminado de tus favoritos.`,
//                 });
//             } else {
//                 toast({
//                     title: "Error",
//                     description: "No se pudo actualizar favoritos",
//                     variant: "destructive",
//                 });
//             }
//         } catch (error) {
//             console.error('Error toggling favorite:', error);
//             toast({
//                 title: "Error",
//                 description: "Ocurrió un error al actualizar favoritos",
//                 variant: "destructive",
//             });
//         } finally {
//             setIsToggling(false);
//         }
//     };

//     const addToWeeklyMenu = (e: React.MouseEvent) => {
//         e.preventDefault();
//         e.stopPropagation();

//         if (onAddToWeeklyMenu) {
//             onAddToWeeklyMenu(id, title);
//         }
//     };

//     // Imagen por defecto si no hay imagen
//     const defaultImage = 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=500&q=80';

//     return (
//         <ContextMenu>
//             <ContextMenuTrigger>
//                 <Link to={`/recipe/${id}`} className="block h-full">
//                     <Card className="recipe-card h-full flex flex-col transition-transform duration-300 hover:shadow-md hover:-translate-y-1 relative">
//                         <div className="relative h-48 overflow-hidden">
//                             <img
//                                 src={image || defaultImage}
//                                 alt={title}
//                                 className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
//                             />
//                             <Badge className="absolute top-3 left-3 bg-white/80 text-food-purple hover:bg-white">
//                                 {categoryMap[category]}
//                             </Badge>
//                             <Button
//                                 variant="ghost"
//                                 size="icon"
//                                 className={`absolute top-3 right-3 h-8 w-8 ${isFav ? 'text-red-500' : 'text-gray-400'
//                                     } hover:text-red-500 bg-white/80 hover:bg-white transition-colors ${isToggling ? 'opacity-50 cursor-not-allowed' : ''
//                                     }`}
//                                 onClick={handleToggleFavorite}
//                                 disabled={isToggling || favoriteLoading}
//                             >
//                                 <Heart className={`h-4 w-4 ${isFav ? 'fill-current' : ''} ${isToggling ? 'animate-pulse' : ''
//                                     }`} />
//                             </Button>
//                         </div>
//                         <CardHeader className="pb-2">
//                             <CardTitle className="text-lg font-bold line-clamp-1">{title}</CardTitle>
//                             <CardDescription className="flex justify-between items-center">
//                                 <span className="text-sm">{time}</span>
//                                 <Badge variant="outline" className={`text-xs ${difficultyMap[difficulty]?.color || 'bg-amber-100 text-amber-800'
//                                     }`}>
//                                     {difficultyMap[difficulty]?.text || 'medio'}
//                                 </Badge>
//                             </CardDescription>
//                         </CardHeader>
//                         <CardContent className="flex-grow">
//                             <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 mb-3">
//                                 <div><span className="font-medium">Cal:</span> {calories}</div>
//                                 <div><span className="font-medium">Proteína:</span> {protein}g</div>
//                                 <div><span className="font-medium">Carbos:</span> {carbs}g</div>
//                                 <div><span className="font-medium">Grasas:</span> {fat}g</div>
//                             </div>

//                             {tags.length > 0 && (
//                                 <div className="flex flex-wrap gap-1 mb-2">
//                                     {tags.slice(0, 3).map((tag) => (
//                                         <Badge
//                                             key={tag}
//                                             variant="outline"
//                                             className={`text-xs ${tagMap[tag]?.color || 'bg-gray-100 text-gray-800'
//                                                 }`}
//                                         >
//                                             {tagMap[tag]?.text || tag}
//                                         </Badge>
//                                     ))}
//                                     {tags.length > 3 && (
//                                         <Badge variant="outline" className="text-xs bg-gray-100 text-gray-600">
//                                             +{tags.length - 3}
//                                         </Badge>
//                                     )}
//                                 </div>
//                             )}

//                             <p className="text-sm text-gray-500 line-clamp-2">
//                                 Una deliciosa receta perfecta para cualquier ocasión.
//                             </p>
//                         </CardContent>
//                         <CardFooter className="pt-2">
//                             <div className="text-xs text-gray-400 flex items-center justify-between w-full">
//                                 <span>4.5 ★★★★☆</span>
//                                 {isFav && (
//                                     <div className="flex items-center gap-1 text-red-500">
//                                         <Heart className="h-3 w-3 fill-current" />
//                                         <span>Favorita</span>
//                                     </div>
//                                 )}
//                             </div>
//                         </CardFooter>
//                     </Card>
//                 </Link>
//             </ContextMenuTrigger>
//             <ContextMenuContent className="w-48">
//                 <ContextMenuItem onClick={addToWeeklyMenu}>
//                     <Calendar className="mr-2 h-4 w-4" />
//                     Agregar al Menú Semanal
//                 </ContextMenuItem>
//                 <ContextMenuItem
//                     onClick={handleToggleFavorite}
//                     disabled={isToggling || !isLoggedIn}
//                 >
//                     <Heart className={`mr-2 h-4 w-4 ${isFav ? 'text-red-500 fill-current' : ''}`} />
//                     {isFav ? 'Quitar de Favoritos' : 'Agregar a Favoritos'}
//                 </ContextMenuItem>
//                 <ContextMenuSeparator />
//                 <ContextMenuItem>
//                     <Plus className="mr-2 h-4 w-4" />
//                     Acciones Rápidas
//                 </ContextMenuItem>
//             </ContextMenuContent>
//         </ContextMenu>
//     );
// };

// export default RecipeCard;